/**
 * Pure H3 cell-set + distance-band math for the Talk tab.
 *
 * Messages carry geo.path of res 7→4 (buildGeo), so the relay can match them at
 * res-8 down to res-4. We therefore scope "my area" at res-6 (fine enough for a
 * neighbourhood, coarse enough to bound the cells[] filter) and "greater region"
 * at res-4 (the coarsest the path supports), sized by a gridDisk ring count k.
 */
import { cellToParent, cellToLatLng, gridDisk, greatCircleDistance, UNITS } from "h3-js";

export const AREA_RES = 6;
export const REGION_RES = 4;
const AVG_EDGE_KM_RES6 = 3.229; // mirrors home-radius.ts AVG_EDGE_KM

/** Greater-region slider steps: approximate radius (km label) → gridDisk ring k at res-4.
 *  ~39 km per ring at res-4 (empirical: Lisbon→Porto = 274 km = 7 rings), so the k
 *  values approximate the km labels; the labels are intentionally coarse. */
export const REGION_STEPS: ReadonlyArray<{ km: number; k: number }> = [
  { km: 50, k: 1 },
  { km: 150, k: 4 },
  { km: 250, k: 6 },
  { km: 400, k: 10 },
];

/** km label for a ring count k (clamped to the widest defined step). */
export function regionKToKm(k: number): number {
  const step = REGION_STEPS.find((s) => s.k === k);
  if (step) return step.km;
  return REGION_STEPS[REGION_STEPS.length - 1].km;
}

/** km label → ring count k (nearest step at or below the km; defaults to the first step). */
export function kmToRegionK(km: number): number {
  let chosen = REGION_STEPS[0];
  for (const s of REGION_STEPS) if (s.km <= km) chosen = s;
  return chosen.k;
}

/** Res-6 cells whose centre is within `radiusKm` of the home cell (my-area scope). */
export function areaCells(homeCell: string, radiusKm: number): string[] {
  const origin = cellToParent(homeCell, AREA_RES);
  const [hlat, hlng] = cellToLatLng(homeCell);
  const k = Math.ceil(radiusKm / AVG_EDGE_KM_RES6) + 1;
  return gridDisk(origin, k).filter((c) => {
    const [clat, clng] = cellToLatLng(c);
    return greatCircleDistance([hlat, hlng], [clat, clng], UNITS.km) <= radiusKm;
  });
}

/** Res-4 cells in a gridDisk of ring count `k` around the home cell (greater-region scope). */
export function regionCells(homeCell: string, k: number): string[] {
  return gridDisk(cellToParent(homeCell, REGION_RES), k);
}

export type Band = "area" | "region" | "distant";

/**
 * Classify a message's res-8 `cell` against the area (res-6) and region (res-4)
 * cell sets. Area wins over region (finest scope). For region, `km` is the
 * great-circle distance home↔message-res-4-centre, rounded to the nearest 25 km.
 */
export function bandFor(
  cell: string,
  areaSet: Set<string>,
  regionSet: Set<string>,
  homeCell: string,
): { band: Band; km?: number } {
  if (!cell) return { band: "distant" };
  if (areaSet.has(cellToParent(cell, AREA_RES))) return { band: "area" };
  const r4 = cellToParent(cell, REGION_RES);
  if (regionSet.has(r4)) {
    const km = greatCircleDistance(cellToLatLng(homeCell), cellToLatLng(r4), UNITS.km);
    return { band: "region", km: Math.round(km / 25) * 25 };
  }
  return { band: "distant" };
}
